import { useLayoutEffect, useRef, useState } from 'react';

export interface SegmentOption<T extends string = string> {
  /** Stable value emitted via onChange. */
  value: T;
  /** Visible label. */
  label: string;
  /** Optional Material Symbols icon name shown before the label. */
  icon?: string;
}

interface SegmentedControlProps<T extends string = string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Visual size. `sm` is good for inline filters, `md` for section headers. */
  size?: 'sm' | 'md';
  /** Stretch to fill the container width (segments share width equally). */
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
}

/**
 * iOS-style segmented control: a pill group with an animated sliding selection
 * indicator. Equal-width segments, fully controlled. Use for filter tabs / view
 * toggles. Keyboard accessible (arrow keys) and reduced-motion aware.
 */
export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = true,
  className = '',
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const activeIndex = Math.max(0, options.findIndex(o => o.value === value));

  // Measure the active segment so the sliding pill can animate to it.
  useLayoutEffect(() => {
    const btn = btnRefs.current[activeIndex];
    const container = containerRef.current;
    if (!btn || !container) return;
    setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [activeIndex, options.length, fullWidth, size]);

  const sizes = size === 'sm'
    ? { pad: 'px-3 py-1', text: 'text-xs', gap: 'p-0.5', icon: 'text-[15px]' }
    : { pad: 'px-4 py-1.5', text: 'text-sm', gap: 'p-1', icon: 'text-[18px]' };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (activeIndex + dir + options.length) % options.length;
    onChange(options[next].value);
    btnRefs.current[next]?.focus();
  };

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`relative inline-flex ${fullWidth ? 'flex w-full' : ''} ${sizes.gap} rounded-xl bg-gray-100 ${className}`}
    >
      {/* Sliding selection indicator */}
      {indicator && (
        <span
          aria-hidden
          className="ios-seg-indicator absolute top-1 bottom-1 rounded-[0.6rem] bg-white shadow-sm ring-1 ring-black/[0.04]"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {options.map((opt, i) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={el => { btnRefs.current[i] = el; }}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(opt.value)}
            className={`relative z-10 flex items-center justify-center gap-1.5 rounded-[0.6rem] font-semibold transition-colors duration-200 active:scale-[0.97] ${sizes.pad} ${sizes.text} ${
              fullWidth ? 'flex-1' : ''
            } ${active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {opt.icon && (
              <span
                className={`material-symbols-outlined ${sizes.icon} leading-none`}
                style={active ? { fontVariationSettings: "'FILL' 1" } : undefined}
              >
                {opt.icon}
              </span>
            )}
            <span className="truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
